<script lang="ts" setup>
import { computed } from 'vue';
import { type JobLocationContext } from '@factoryfixinc/ats-interfaces';
import {
  type WorkPerformed,
  type WorkerLives,
  locationContextFor,
  workerLivesFor,
} from '@/core/jobs/copilot-activation/location-context.utils';

import OptionCard from './OptionCard.vue';

const props = withDefaults(
  defineProps<{
    modelValue: JobLocationContext | null;
    // Owned by the parent: "In the field" with no second answer yet resolves to a null
    // context, which on its own would read back as the worksite answer.
    workPerformed: WorkPerformed;
    isDisabled?: boolean;
  }>(),
  { isDisabled: false },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: JobLocationContext | null): void;
  (e: 'update:workPerformed', value: WorkPerformed): void;
}>();

// Re-choosing "In the field" must keep an already resolved lives_near / nationwide answer.
const workerLives = computed<WorkerLives | null>(() =>
  props.workPerformed === 'field' ? workerLivesFor(props.modelValue) : null,
);

function select(value: WorkPerformed) {
  if (props.isDisabled) return;
  emit('update:workPerformed', value);
  emit('update:modelValue', locationContextFor(value, workerLives.value));
}
</script>

<template>
  <div
    data-test="work-performed-question"
    class="mb-4"
    :class="{ 'pointer-events-none opacity-60': isDisabled }"
  >
    <p class="mb-3 font-sans text-xs font-bold leading-[18px] text-shade-900">
      Where is the work performed?*
    </p>
    <div class="flex flex-wrap items-stretch gap-4">
      <OptionCard
        test-id="work-performed-worksite"
        title="At one worksite"
        hint="Same address every day."
        :selected="workPerformed === 'worksite'"
        :disabled="isDisabled"
        @select="select('worksite')"
      />
      <OptionCard
        test-id="work-performed-field"
        title="In the field"
        hint="Customer sites, job sites, projects, or several company facilities. No single worksite."
        :selected="workPerformed === 'field'"
        :disabled="isDisabled"
        @select="select('field')"
      />
    </div>
  </div>
</template>
